import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { entregarTranscricao } from '../../../../../../services/TranscricaoBeneficiarioService';
import {
  buildTranscricaoConsultaPath,
  resolveTranscricaoResourceToken,
} from '../../../../../../interfaces/entities/Transcricao/transcricaoResourceToken';
import { gravarTranscricaoAnexos } from '../../../transcricaoAnexoHelpers';
import { TranscricaoAnexo } from '../../../transcricaoTypes';
import { useTranscricaoCarregamento } from '../../../hooks/useTranscricaoCarregamento';
import { normalizeCodStatusTranscricao } from '../../../transcricaoStatusUtils';
import { getPendenciaMensagemCardFromHistoricoStatus } from '../../../utils/resolverPendenciaUtils';
import { getTranscricaoErrorMessage } from '../../../utils/transcricaoErrorUtils';

const MAX_JUSTIFICATIVA = 1000;

export const useResolverPendenciaPage = () => {
  const { idTranscricao: idParam } = useParams<{ idTranscricao: string }>();
  const navigate = useNavigate();
  const idTranscricao = resolveTranscricaoResourceToken(idParam);

  const detalhesPath = idTranscricao
    ? buildTranscricaoConsultaPath(idTranscricao)
    : '/guiasEautorizacoes/transcricaoSolicitacao/consultar';

  const { loading, erro: erroCarregamento, detalhe, historicoStatus } = useTranscricaoCarregamento(
    idTranscricao,
  );

  const [enviando, setEnviando] = useState(false);
  const [anexos, setAnexos] = useState<TranscricaoAnexo[]>([]);
  const [justificativa, setJustificativa] = useState('');
  const [showModalErro, setShowModalErro] = useState(false);
  const [tituloModalErro, setTituloModalErro] = useState('');
  const [subTituloModalErro, setSubTituloModalErro] = useState('');
  const [showModalSucesso, setShowModalSucesso] = useState(false);

  useEffect(() => {
    if (loading || !idTranscricao || erroCarregamento || !detalhe) return;

    const statusResolvido =
      historicoStatus?.codStatusTranscricao ?? detalhe.codStatusTranscricao ?? null;

    if (normalizeCodStatusTranscricao(statusResolvido) !== 'SOLINF') {
      navigate(detalhesPath, { replace: true });
    }
  }, [detalhe, detalhesPath, erroCarregamento, historicoStatus, idTranscricao, loading, navigate]);

  const { mensagem: mensagemPendencia, dataMensagem } = useMemo(() => {
    const card = getPendenciaMensagemCardFromHistoricoStatus(historicoStatus);
    return {
      mensagem:
        card.mensagem
        || detalhe?.observacao?.trim()
        || detalhe?.motivoTranscricao?.trim()
        || undefined,
      dataMensagem: card.dataMensagem,
    };
  }, [historicoStatus, detalhe?.motivoTranscricao, detalhe?.observacao]);

  const openModalErro = (title: string, subTitle: string) => {
    setTituloModalErro(title);
    setSubTituloModalErro(subTitle);
    setShowModalErro(true);
  };

  const handleConcluir = async () => {
    if (!idTranscricao || enviando) return;

    const justificativaTrim = justificativa.trim().slice(0, MAX_JUSTIFICATIVA);
    if (!justificativaTrim) {
      openModalErro(
        'Justificativa obrigatória',
        'Informe uma justificativa para concluir a resolução da pendência.',
      );
      return;
    }

    setEnviando(true);

    try {
      if (anexos.length > 0) {
        const anexosGravados = await gravarTranscricaoAnexos(idTranscricao, anexos);
        if (!anexosGravados.ok) {
          openModalErro('Erro ao salvar anexos', anexosGravados.message);
          return;
        }
      }

      const resultado = await entregarTranscricao({
        idTranscricao,
        obs: justificativaTrim,
      });

      if (!resultado.ok) {
        openModalErro('Erro ao concluir pendência', resultado.message);
        return;
      }

      setShowModalSucesso(true);
    } catch (error: unknown) {
      openModalErro('Erro', getTranscricaoErrorMessage(error));
    } finally {
      setEnviando(false);
    }
  };

  const handleEntendiSucesso = () => {
    setShowModalSucesso(false);
    navigate(detalhesPath);
  };

  const podeResolver =
  !erroCarregamento
  && !!detalhe
  && normalizeCodStatusTranscricao(historicoStatus?.codStatusTranscricao) === 'SOLINF';

  return {
    loading,
    enviando,
    erro: erroCarregamento,
    detalhe,
    anexos,
    setAnexos,
    justificativa,
    setJustificativa,
    maxJustificativa: MAX_JUSTIFICATIVA,
    mensagemPendencia,
    dataMensagem,
    showModalErro,
    setShowModalErro,
    tituloModalErro,
    subTituloModalErro,
    showModalSucesso,
    podeResolver,
    detalhesPath,
    handleConcluir,
    handleEntendiSucesso,
  };
};
